import { html, LitElement } from 'lit-element';
import { unsafeSVG } from 'lit-html/directives/unsafe-svg';
import { sy_shapes_close_large as close } from '@aab/sc-aab-icon-set';
import Styles from './styles.scss';

// Some keys require IE/Edge specific alternatives (ie. Escape/Esc)
const KEY_CODES = { escape: ['Escape', 'Esc'], tab: 'Tab' };

export default class AabModal extends LitElement {
  constructor() {
    super();
    this.modalSize='large';
    this.open = false;
    this.focusedEle= '';
    this.firstFocusableElement='';
    this.lastFocusableElement='';
    this.hideFooter = false;
  }

  static get properties() {
    return {
      modalSize: {
        type: String,
        attribute: 'modal-size',
        reflect: true,
      },
      open: {
        type: Boolean,
        reflect: true,
        attribute: false,
      },
      hideFooter: {
        type: Boolean,
        attribute: false
      }
    };
  }

  static get styles() {
    return [Styles];
  }


  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', (e) => this.handleKeyDownEvents(e));
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', (e) => this.handleKeyDownEvents(e));
    super.disconnectedCallback();
  }

  firstUpdated() {
    this.renderFooter();
    this.firstFocusableElement = this.shadowRoot.querySelector('.aab-modal-close');
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const focusableContent = this.querySelectorAll(focusableElements);
    this.lastFocusableElement = focusableContent[focusableContent.length - 1];
  }

  updated() {
    const modal = this.shadowRoot.querySelector('#modal_dialog');
    modal.focus();
  }

  openModal() {
    this.open = true;
    document.body.style.overflow = 'hidden';

    const modalOpenEvent = new CustomEvent('modal-open', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(modalOpenEvent);
  }

  handleKeyDownEvents(e) {
    this.focusedEle = this.shadowRoot.activeElement;
    if (this.focusedEle === null && document.activeElement) {
      if (document.activeElement.shadowRoot) {
        this.focusedEle = document.activeElement.shadowRoot.activeElement;
      } else {
        this.focusedEle = document.activeElement;
      }
    }

    if(this.open && KEY_CODES.escape.includes(e.key)) {
      this.closeModal(e);
      return
    }

    if(e.key === KEY_CODES.tab) {
      this.trapFocus(e);
    }
  }

  trapFocus(e) {
    if (e.shiftKey) {
      if (this.focusedEle === this.firstFocusableElement) {
          this.lastFocusableElement.focus();
          e.preventDefault();
        }
    } else {
      if (this.focusedEle && this.focusedEle === this.lastFocusableElement) {
         this.firstFocusableElement.focus();
         e.preventDefault()
       }
    }
  }

  renderFooter() {
    const footer = this.shadowRoot.querySelector('#footer');
    const footerData = footer.assignedNodes();
    if(footerData.length < 1) {
      this.hideFooter = true;
    }
  }

  closeModal(e, from) {
    e.stopPropagation();
    if(from === 'modal_container') {
      return;
    }
    this.open = false;
    const modalCloseEvent = new CustomEvent('modal-close', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(modalCloseEvent);
    document.body.style.overflow = 'inherit';
  }

  render() {
    return html`
      <div class="aab-modal-overlay opacity-70 ${this.open ? '' : 'closed'}"></div>
      <div class="aab-modal overflow-y-auto ${this.open ? '' : 'closed'}"
        id="modal-scrollable"
        role="dialog"
        aria-labelledby="modal-scrollable__modal_header"
        aria-describedby="modal-scrollable__modal_body"
        aria-modal="${this.open}" @click="${(e) => this.closeModal(e)}" id="modal">
        <div class="aab-modal-container d-flex align-items-center modal-${this.modalSize} position-relative" @click="${(e) => this.closeModal(e, 'modal_container')}">
           <div tabIndex="0" class="aab-modal-dialog d-flex flex-column position-relative rounded-4 shadow-modal overflow-hide" id="modal_dialog">
               <div class="modal-header d-flex align-items-start justify-content-between" id="modal-scrollable__modal_header">
                 <slot name="modal-title"></slot>
                 <button aria-label="close icon" @click="${(e) => this.closeModal(e)}" class="aab-modal-close position-relative m-1">
                    ${unsafeSVG(close)}
                 </button>
               </div>
               <div class="aab-modal-content position-relative overflow-y-auto" id="modal-scrollable__modal_body">
                   <slot name="modal-content"></slot>
               </div>
               ${!this.hideFooter ? html`<div class="aab-modal-footer" id="modal-scrollable__modal_footer">
                   <slot name="modal-footer" id="footer"></slot>
               </div>` : ''}
           </div>
        </div>
      </div>
    `;
  }
}
